import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { catchError, map, Observable, of, Subscription, throwError } from 'rxjs';
import { CompilaSintesiSostenibilitaComponent } from 'src/app/components/compila-sintesi-sostenibilita/compila-sintesi-sostenibilita.component';
import { SpinnerOverlayComponent } from 'src/app/components/spinner-overlay/spinner-overlay.component';
import { TabellaDinamica } from 'src/app/components/tabella-cfg-dinamica/tabella-cfg-dinamica.component';
import { Ambito } from 'src/app/services/ambiti/ambiti.service';
import { ReportVsmeService } from 'src/app/services/report-vsme/report-vsme.service';
import { TemaService } from 'src/app/services/tema/tema.service';
import { OpzioneTematica } from 'src/app/services/tematiche/tematiche.service';

// ─── VSME Types (mirrored from dialog-compila-sintesi-sostenibilita) ──────────

/** Struttura base comune a tutte le risposte VSME. */
interface RispostaBase {
  idElemento: string;
}

/** Risposta per elementi di tipo KPI. */
interface RispostaKpi extends RispostaBase {
  idUnitaDiRiferimento: string;
  idCategoriaUnitaDiMisura: string;
  risposta: any[];
}

/** Motivazione per la quale la risposta non è stata tratta. */
interface MotivazioneRisposta extends RispostaBase {
  infoNonDisponibile?: { checked: false; risposta: string };
  infoNonRilevante?: { checked: false; risposta: string };
}

/** Risposta per elementi di tipo azione o checkbox. */
interface RispostaAzione extends RispostaBase {
  risposta: string;
}

/** Risposta per elementi di tipo tabella dinamica. */
interface RispostaDynamicTable extends RispostaBase {
  risposta: any;
  colonne: any[];
  righe: any[];
  totaleRighe: boolean;
  totaleColonne: boolean;
  titoloTabella: string;
}

/** Unione discriminata di tutti i tipi di risposta VSME possibili. */
type RispostaVsme = RispostaKpi | MotivazioneRisposta | RispostaAzione | RispostaDynamicTable;

// ─── Request item built per ogni sotto-opzione ────────────────────────────────

interface VsmeRequestItem {
  idAmbito: string;
  idTematica: string;
  idElemento: string;
  chiaveRisposta: string;
  idUnitaDiRiferimento: string;
  idCategoriaUnitaDiMisura: string;
  risposta: any;
  dynamicTable?: TabellaDinamica;
  // VSME: motivazioni per risposta non fornita
  infoNonDisponibile?: { checked: false; risposta: string };
  infoNonRilevante?: { checked: false; risposta: string };
}

@Component({
  selector: 'app-report-vsme-esterna',
  standalone: false,
  templateUrl: './report-vsme-esterna.component.html',
  styleUrl: './report-vsme-esterna.component.scss',
})
export class ReportVsmeEsternaComponent implements AfterViewInit, OnDestroy {
  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;
  @ViewChild(CompilaSintesiSostenibilitaComponent)
  compilaSintesiSostenibilita!: CompilaSintesiSostenibilitaComponent;

  public completato: boolean = false;
  public temporaryToken!: string;
  public descrizione: string = '';
  public messaggio: string = '';

  public compilazione: any;
  public idReportVsme: string = '';
  private idAzienda: string = '';
  public landingPage: string = '';
  public isAnteprima: boolean = false;
  public isUtenteGo: boolean = false;
  private _subTema: Subscription | undefined;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly reportVsmeService: ReportVsmeService,
    public readonly temaService: TemaService,
  ) {
    this.ctrIsUtenteGo();
  }

  ngAfterViewInit(): void {
    this.parametriDaURL();
  }

  ngOnDestroy(): void {
    this._subTema?.unsubscribe();
  }

  public parametriDaURL() {
    this.spinnerOver.show();
    this.route.queryParams.subscribe((params) => {
      this.temporaryToken = params['temporaryToken'];
      this.idAzienda = params['idA'];
      this.idReportVsme = params['idR'];
      if (this.temporaryToken) {
        this._getSurveyByTemporaryToken();
      } else if (this.idReportVsme && this.idAzienda) {
        this._anteprimaReportVsme();
      }
    });
  }

  private _anteprimaReportVsme() {
    this.isAnteprima = true;
    this.spinnerOver.show();
    this.reportVsmeService
      .getAnteprimaReportVsmeCompilato(this.idReportVsme, this.idAzienda)
      .subscribe({
        next: (esito) => {
          this.spinnerOver.hide();
          this.compilazione = esito.questionarioDaCompilare;
          this.landingPage = esito.landing;
        },
        error: (err) => {
          console.error(err);
          this.spinnerOver.hide();
        },
      });
  }

  private _getSurveyByTemporaryToken() {
    this.isAnteprima = false;
    this.reportVsmeService.getElementiEsterniDaCompilare(this.temporaryToken).subscribe({
      next: (esito) => {
        this.compilazione = esito.questionarioDaCompilare;
        this.landingPage = esito.landing;
        this.spinnerOver.hide();
      },
      error: (err) => {
        console.error(err);
        this.messaggio = err?.error?.message || 'Nessun dato trovato';
        this.completato = true;
        this.spinnerOver.hide();
      },
    });
  }

  loading = (loading: boolean) => {
    loading ? this.spinnerOver.show() : this.spinnerOver.hide();
  };

  salva = (compilazione: any, isImportazione?: boolean): Observable<any> => {
    if (!compilazione) return of();

    this.spinnerOver.show();

    const arrayRequest: VsmeRequestItem[] = [];

    (this.compilazione.ambiti as Ambito[]).forEach((ambito) => {
      ambito.tematiche.forEach((tematica) => {
        const baseObjCall = {
          idAmbito: ambito.id,
          idTematica: tematica.id,
          idElemento: '',
          chiaveRisposta: '',
          idUnitaDiRiferimento: '',
          idCategoriaUnitaDiMisura: '',
          risposta: '',
        };

        tematica.risposteVsme.forEach((rispVsme) => {
          baseObjCall.chiaveRisposta = rispVsme.chiave;

          rispVsme.sottoOpzioni.forEach((sottoOpz: OpzioneTematica) => {
            // ── VSME: build request item per sotto-opzione ──────────────────
            const item = this._buildVsmeRequestItem(baseObjCall, sottoOpz);
            if (item) arrayRequest.push(item);
          });
        });
      });
    });

    // ── VSME: filtra solo gli elementi con una risposta valida ───────────────
    const arrayRequestFiltered = arrayRequest.filter((elem) => this._hasValidRisposta(elem));

    return this.reportVsmeService
      .postElementiEsterniDaCompilare(this.temporaryToken, arrayRequestFiltered)
      .pipe(
        map(() => {
          this.spinnerOver.hide();
          if (!isImportazione) {
            this.messaggio = 'Le tue risposte sono state salvate. Grazie.';
            this.completato = true;
          }
        }),
        catchError((err) => {
          this.spinnerOver.hide();
          console.error(err);
          return throwError(() => err);
        }),
      );
  };

  // ─── VSME Builders ──────────────────────────────────────────────────────────

  /**
   * Costruisce il request item per una singola sotto-opzione VSME,
   * replicando la logica di `_manageRispostaVsme` del componente dialog.
   *
   * Gestisce:
   * - `notRisposta`: pulisce i valori e aggiunge motivazioni (infoNonDisponibile /
   *   infoNonRilevante) esattamente come fa il componente dialog.
   * - tipi `kpi`, `azione`, `checkbox`, `dynamic_table`.
   *
   * @param base Campi comuni (idAmbito, idTematica, chiaveRisposta, ecc.).
   * @param sottoOpz Sotto-opzione della risposta VSME.
   * @returns Request item completo, oppure `undefined` se il tipo non è gestito.
   */
  private _buildVsmeRequestItem(
    base: Omit<VsmeRequestItem, 'idElemento' | 'risposta'>,
    sottoOpz: OpzioneTematica,
  ): VsmeRequestItem | undefined {
    const {
      tipo,
      id,
      idUnitaDiRiferimento,
      idCategoriaUnitaDiMisura,
      infoNonDisponibile,
      infoNonRilevante,
      notRisposta,
    } = sottoOpz;

    let { risposta, valoriKpi = [], valoriTarget = [] } = sottoOpz;

    const idElemento = id ?? '';

    // ── VSME: se notRisposta, pulisce i valori e raccoglie le motivazioni ───
    const motivazione = notRisposta ? { infoNonDisponibile, infoNonRilevante } : {};
    if (notRisposta) {
      valoriKpi = valoriKpi.map((v: any) => ({ ...v, valoreAttribuito: undefined }));
      valoriTarget = valoriTarget.map((v: any) => ({ ...v, valoreAttribuito: undefined }));
      risposta = '';
    }

    switch (tipo) {
      case 'kpi':
        return {
          ...base,
          idElemento,
          idUnitaDiRiferimento: idUnitaDiRiferimento ?? '',
          idCategoriaUnitaDiMisura: idCategoriaUnitaDiMisura ?? '',
          risposta: [...valoriKpi, ...valoriTarget],
          ...motivazione,
        };

      case 'azione':
      case 'checkbox':
        return {
          ...base,
          idElemento,
          risposta,
          ...motivazione,
        };

      case 'dynamic_table': {
        // ── VSME: costruisce TabellaDinamica come fa _buildRispostaDynamicTable
        const dynamicTable = this._buildRispostaDynamicTable(sottoOpz);
        return {
          ...base,
          idElemento,
          risposta,
          dynamicTable,
          ...motivazione,
        };
      }

      default:
        return undefined;
    }
  }

  /**
   * Costruisce un oggetto {@link TabellaDinamica} dalla sotto-opzione,
   * mappando colonne, righe e metadati della tabella.
   * Replica `_buildRispostaDynamicTable` del componente dialog.
   *
   * @param sottoOpzione Sotto-opzione di tipo `dynamic_table`.
   * @returns Oggetto TabellaDinamica pronto per il POST.
   */
  private _buildRispostaDynamicTable(sottoOpzione: OpzioneTematica): TabellaDinamica {
    const {
      id,
      colonne = [],
      righe = [],
      totaleRighe,
      totaleColonne,
      titoloTabella,
    } = sottoOpzione;
    return {
      id: id ?? '',
      colonne,
      righe,
      totaleRighe,
      totaleColonne,
      titoloTabella,
    };
  }

  /**
   * Verifica che un request item abbia una risposta valida da inviare.
   * Replica il filtro applicato in `salva()` nella versione originale,
   * esteso per coprire anche il caso `notRisposta` (motivazioni VSME):
   * in quel caso l'item è sempre incluso perché porta le motivazioni.
   *
   * @param elem Request item da valutare.
   * @returns `true` se l'item deve essere incluso nel POST.
   */
  private _hasValidRisposta(elem: VsmeRequestItem): boolean {
    // ── VSME: se è presente una motivazione (notRisposta), includi sempre ───
    if (elem.infoNonDisponibile || elem.infoNonRilevante) return true;

    if (elem.risposta !== null && elem.risposta !== undefined) {
      if (Array.isArray(elem.risposta)) {
        return elem.risposta.some(
          (risp: any) => risp?.valoreAttribuito !== null && risp?.valoreAttribuito !== undefined,
        );
      }
      return true;
    }

    // ── VSME: per le tabelle dinamiche controlla che almeno una cella sia compilata
    if (elem.dynamicTable?.righe?.length) {
      return elem.dynamicTable.righe.some((riga: any) => {
        const celleMap = Object.values(riga.celle || {})[0];
        return (
          celleMap &&
          Object.values(celleMap).some(
            (cella: any) => typeof cella === 'string' && cella.trim() !== '',
          )
        );
      });
    }

    return false;
  }

  // ─── Theme ──────────────────────────────────────────────────────────────────

  private ctrIsUtenteGo() {
    this._subTema = this.temaService.temaUpdated.subscribe((tipoInstallazione) => {
      this.isUtenteGo = tipoInstallazione === 'SOSTENIBILE_GO';
    });
  }
}
